
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Sliders, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';

import PropertyCard from '@/components/ui/PropertyCard';
import { Skeleton } from '@/components/ui/skeleton';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { getProperties, getFeaturedProperties, getTrendingProperties, getUpcomingProperties } from '@/services/property';

import {
  propertyTypes,
  localities,
  amenities,
  bedrooms as bedroomOptions,
  pagination
} from '@/constants';
import SEO from '@/components/seo/seo';

const Properties = () => {
  const [searchParams] = useSearchParams();
  const isFirstRender = useRef(true);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [displayedProperties, setDisplayedProperties] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 15000000]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [selectedBedrooms, setSelectedBedrooms] = useState<string | null>(null);
  const [listingType, setListingType] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState('newest');
  const [filteredCount, setFilteredCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProperties = async (page = 1, overrideParams: any = {}) => {
    setIsLoading(true);
    try {
      // Determine which filter type is active (URL takes precedence if we are in syncing phase, but usually state tracks it)
      // Check if we have an override for the filter type (from URL parsing)
      const currentFilterType = overrideParams.activeFilter !== undefined ? overrideParams.activeFilter : activeFilter;

      const currentPriceRange = overrideParams.priceRange ?? priceRange;

      const uiListingType = overrideParams.listingType ?? listingType;

      const params = {
        page,
        limit: pagination.limit,
        searchQuery: overrideParams.searchQuery ?? searchQuery,
        'priceRange[min]': currentPriceRange[0],
        'priceRange[max]': currentPriceRange[1],
        propertyTypes: overrideParams.selectedPropertyTypes ?? selectedPropertyTypes,
        locations: overrideParams.selectedLocations ?? selectedLocations,
        amenities: overrideParams.selectedAmenities ?? selectedAmenities,
        bedrooms: overrideParams.selectedBedrooms ?? selectedBedrooms,
        listingType: uiListingType === 'OFF-PLAN' ? undefined : uiListingType,
        availability: uiListingType === 'OFF-PLAN' ? 'OFF-PLAN' : undefined,
        sort: overrideParams.sortOption ?? sortOption
      };

      let response;
      if (currentFilterType === 'trending') {
        response = await getTrendingProperties(params);
      } else if (currentFilterType === 'featured') {
        response = await getFeaturedProperties(params);
      } else if (currentFilterType === 'upcoming' || currentFilterType === 'off-plan') {
        // Using 'off-plan' derived from previous logic code inspection
        response = await getUpcomingProperties(params);
      } else {
        response = await getProperties(params);
      }

      const { data, pagination: paginationData } = response;

      if (Array.isArray(data)) {
        setDisplayedProperties(data);
        // If the API returns total count, we can use it. 
        // Assuming paginationData has totalItems and totalPages based on common patterns.
        setFilteredCount(paginationData?.totalProperties || data.length); // Fallback
        setTotalPages(paginationData?.totalPages || 1);
        setCurrentPage(paginationData?.page || page);
      } else {
        setDisplayedProperties([]);
        setFilteredCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      console.log("Error while fetching properties", error);
      setDisplayedProperties([]);
      setFilteredCount(0);
    } finally {
      setIsLoading(false);
    }
  };



  // Sync state with URL and Fetch
  useEffect(() => {
    const filter = searchParams.get('filter');
    const typeParam = searchParams.get('type');
    const locationParam = searchParams.get('location');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const searchParam = searchParams.get('search');
    const listingTypeParam = searchParams.get('listingType');

    setActiveFilter(filter);
    setListingType(listingTypeParam);

    if (searchParam) {
      setSearchQuery(searchParam);
    }

    let newPropertyTypes = [];
    if (typeParam) {
      const match = propertyTypes.find(t => t.value === typeParam || t.label === typeParam);
      newPropertyTypes = [match ? match.value : typeParam];
    }
    setSelectedPropertyTypes(newPropertyTypes);

    let newLocations = [];
    if (locationParam) {
      const match = localities.find(l => l.value === locationParam || l.label === locationParam);
      newLocations = [match ? match.value : locationParam];
    }
    setSelectedLocations(newLocations);

    let newPriceRange = [0, 15000000];
    if (minPrice || maxPrice) {
      newPriceRange = [
        minPrice ? Number(minPrice) : 0,
        maxPrice ? Number(maxPrice) : 15000000
      ];
    }
    setPriceRange(newPriceRange as [number, number]);

    // Fetch with these new values immediately
    fetchProperties(1, {
      activeFilter: filter,
      searchQuery: searchParam || '',
      selectedPropertyTypes: newPropertyTypes,
      selectedLocations: newLocations,
      priceRange: newPriceRange,
      listingType: listingTypeParam
    });

    // eslint-disable-next-line
  }, [searchParams]);

  // Apply all filters (Server Side)
  const applyFilters = () => {
    fetchProperties(1);
    setFilterDrawerOpen(false);
  };

  const handlePageChange = (page: number) => {
    fetchProperties(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Trigger filter/sort when sort option changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchProperties(1);
    // eslint-disable-next-line
  }, [sortOption]);

  const resetFilters = () => {
    setSearchQuery('');
    setPriceRange([0, 15000000]);
    setSelectedLocations([]);
    setSelectedPropertyTypes([]);
    setSelectedAmenities([]);
    setSelectedBedrooms(null);

    // Reset to API base filtered list
    // Reset local state
    setPriceRange([0, 15000000]);
    setSelectedLocations([]);
    setSelectedPropertyTypes([]);
    setSelectedAmenities([]);
    setSelectedBedrooms(null);
    setListingType(null);
    setSearchQuery('');

    // Fetch default
    fetchProperties(1, {
      searchQuery: '',
      priceRange: [0, 15000000],
      selectedLocations: [],
      selectedPropertyTypes: [],
      selectedAmenities: [],
      selectedBedrooms: null,
      listingType: null
    });
  };

  // Toggle location selection
  const toggleLocation = (location: string) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  // Toggle property type selection
  const togglePropertyType = (type: string) => {
    setSelectedPropertyTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Toggle amenity selection
  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity)
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  // Format price for display
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <>
      {/* SEO */}
      <SEO
        title="Luxury Properties in Dubai | Urban Oasis UAE"
        description="Browse luxury villas, apartments and investment properties in Dubai."
        url="/properties"
        image="/image/dubai-palm-jumeirah-island-aerial-view.avif"
        keywords="Luxury Properties, Dubai Real Estate, Villas for Sale, Apartments for Rent, Investment Properties"
      />



      {/* Page Header */}
      <section className="pt-32 pb-10 md:pt-40 md:pb-12 bg-secondary/50">
        <div className="container mx-auto px-4 md:px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Properties</h1>
          <div className="flex flex-wrap items-center text-sm text-muted-foreground">
            <span>Home</span>
            <span className="mx-2">/</span>
            <span className="font-medium text-foreground">Properties</span>
            {activeFilter && (
              <>
                <span className="mx-2">/</span>
                <span className="capitalize">{activeFilter}</span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 border-b">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:flex-none">
                <Input
                  type="text"
                  placeholder="Search by property name or location"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full md:w-80"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
              <Button
                variant="default"
                onClick={applyFilters}
              >
                Search
              </Button>
            </div>

            <div className="flex gap-2 w-full md:w-auto md:justify-end">
              <Button
                variant="outline"
                className="md:hidden flex-1"
                onClick={() => setFilterDrawerOpen(true)}
              >
                <Sliders className="h-4 w-4 mr-2" />
                Filters
              </Button>

              <div className="flex items-center w-full md:w-auto">
                <span className="text-sm mr-2 hidden md:inline whitespace-nowrap">Sort by:</span>
                <Select value={sortOption} onValueChange={setSortOption}>
                  <SelectTrigger className="w-full md:w-[180px]" aria-label="Sort properties">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="price-asc">Price (Low to High)</SelectItem>
                    <SelectItem value="price-desc">Price (High to Low)</SelectItem>
                    <SelectItem value="beds-asc">Beds (Low to High)</SelectItem>
                    <SelectItem value="beds-desc">Beds (High to Low)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Filter Pills */}
          {(selectedLocations.length > 0 || selectedPropertyTypes.length > 0 || selectedBedrooms || listingType || priceRange[0] > 0 || priceRange[1] < 15000000) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedLocations.map(location => (
                <div
                  key={location}
                  className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm"
                >
                  {localities.find(l => l.value === location)?.label || location}
                  <button
                    onClick={() => toggleLocation(location)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {selectedPropertyTypes.map(type => (
                <div
                  key={type}
                  className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm"
                >
                  {propertyTypes.find(t => t.value === type)?.label || type}
                  <button
                    onClick={() => togglePropertyType(type)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {selectedBedrooms && (
                <div className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm">
                  {bedroomOptions.find(b => b.value === selectedBedrooms)?.label || selectedBedrooms}
                  <button
                    onClick={() => setSelectedBedrooms(null)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {listingType && (
                <div className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm">
                  {listingType === 'SALE' ? 'For Sale' : listingType === 'RENT' ? 'For Rent' : 'Off-Plan'}
                  <button
                    onClick={() => setListingType(null)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {(priceRange[0] > 0 || priceRange[1] < 15000000) && (
                <div className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm">
                  {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
                  <button
                    onClick={() => setPriceRange([0, 15000000])}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              {selectedAmenities.map(amenity => (
                <div
                  key={amenity}
                  className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-sm"
                >
                  {amenities.find(a => a.value === amenity)?.label || amenity}
                  <button
                    onClick={() =>
                      setSelectedAmenities(prev => prev.filter(a => a !== amenity))
                    }
                    className="ml-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}


              <button
                onClick={resetFilters}
                className="text-sm text-muted-foreground hover:text-foreground ml-2"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar - Desktop */}
            <div className="hidden lg:block w-full lg:w-64 shrink-0">
              <div className="sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto pr-2">
                <div className="mb-8 flex flex-col items-center space-y-4">
                  <h3 className="text-lg font-semibold mb-4 self-start">Filters</h3>
                  <Button
                    variant="outline"
                    className="w-[90%]"
                    onClick={resetFilters}
                  >
                    Reset All
                  </Button>
                  <Button
                    className="w-[90%]"
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </Button>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {/* Listing Type Filter */}
                  <AccordionItem value="listing-type">
                    <AccordionTrigger className="text-base font-medium">Listing Type</AccordionTrigger>
                    <AccordionContent>
                      <div className="flex gap-2 px-1 py-2">
                        <Button
                          variant={listingType === 'SALE' ? "default" : "outline"}
                          className="flex-1"
                          onClick={() => setListingType(listingType === 'SALE' ? null : 'SALE')}
                        >
                          Buy
                        </Button>
                        <Button
                          variant={listingType === 'RENT' ? "default" : "outline"}
                          className="flex-1"
                          onClick={() => setListingType(listingType === 'RENT' ? null : 'RENT')}
                        >
                          Rent
                        </Button>
                        <Button
                          variant={listingType === 'OFF-PLAN' ? "default" : "outline"}
                          className="flex-1"
                          onClick={() => setListingType(listingType === 'OFF-PLAN' ? null : 'OFF-PLAN')}
                        >
                          Off-Plan
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Price Range Filter */}
                  <AccordionItem value="price">
                    <AccordionTrigger className="text-base font-medium">Price Range</AccordionTrigger>
                    <AccordionContent>
                      <div className="px-1 py-4">
                        <Slider
                          defaultValue={[0, 15000000]}
                          value={priceRange}
                          onValueChange={(value) => setPriceRange(value as [number, number])}
                          min={0}
                          max={15000000}
                          step={100000}
                          className="mb-6"
                        />
                        <div className="flex justify-between text-sm">
                          <span>{formatPrice(priceRange[0])}</span>
                          <span>{formatPrice(priceRange[1])}</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Property Type Filter */}
                  <AccordionItem value="property-type">
                    <AccordionTrigger className="text-base font-medium">Property Type</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 px-1 py-2">
                        {propertyTypes.map(type => (
                          <div key={type.value} className="flex items-center">
                            <Checkbox
                              id={`type-${type.value}`}
                              checked={selectedPropertyTypes.includes(type.value)}
                              onCheckedChange={() => togglePropertyType(type.value)}
                            />
                            <label
                              htmlFor={`type-${type.value}`}
                              className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {type.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Location Filter */}
                  <AccordionItem value="location">
                    <AccordionTrigger className="text-base font-medium">Location</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 px-1 py-2">
                        {localities.map(location => (
                          <div key={location.value} className="flex items-center">
                            <Checkbox
                              id={`location-${location.value}`}
                              checked={selectedLocations.includes(location.value)}
                              onCheckedChange={() => toggleLocation(location.value)}
                            />
                            <label
                              htmlFor={`location-${location.value}`}
                              className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {location.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Bedrooms Filter */}
                  <AccordionItem value="bedrooms">
                    <AccordionTrigger className="text-base font-medium">Bedrooms</AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-3 gap-2 px-1 py-2">
                        {bedroomOptions.map(bed => (
                          <Button
                            key={bed.value}
                            variant={selectedBedrooms === bed.value ? "default" : "outline"}
                            className="text-sm h-9"
                            onClick={() => setSelectedBedrooms(selectedBedrooms === bed.value ? null : bed.value)}
                          >
                            {bed.label}
                          </Button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Amenities Filter */}
                  <AccordionItem value="amenities">
                    <AccordionTrigger className="text-base font-medium">Amenities</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 px-1 py-2">
                        {amenities.map(amenity => (
                          <div key={amenity.value} className="flex items-center">
                            <Checkbox
                              id={`amenity-${amenity.value}`}
                              checked={selectedAmenities.includes(amenity.value)}
                              onCheckedChange={() => toggleAmenity(amenity.value)}
                            />
                            <label
                              htmlFor={`amenity-${amenity.value}`}
                              className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {amenity.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

              </div>
            </div>

            {/* Properties Grid */}
            <div className="pl-8 w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                  Showing {filteredCount} Properties {activeFilter && `(${activeFilter})`}
                </h2>

              </div>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="rounded-3xl overflow-hidden border bg-card">
                      {/* Property Image Skeleton */}
                      <div className="relative">
                        <Skeleton className="w-full h-[300px]" />
                        {/* Status Badge Skeleton */}
                        <div className="absolute top-4 right-4">
                          <Skeleton className="h-6 w-20 rounded-full" />
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-6 md:p-7">
                        {/* Location */}
                        <div className="flex items-center gap-2 mb-3">
                          <Skeleton className="h-5 w-5 rounded-full" />
                          <Skeleton className="h-4 w-40" />
                        </div>

                        {/* Title */}
                        <div className="h-14 mb-3">
                          <Skeleton className="h-7 w-full mb-2" />
                          <Skeleton className="h-7 w-3/4" />
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-2 mb-5">
                          <Skeleton className="h-9 w-40" />
                          <Skeleton className="h-4 w-10" />
                        </div>

                        {/* Property Stats (Beds, Baths, Area) */}
                        <div className="flex items-center justify-between border-t border-border/50 pt-5 gap-4 mb-5">
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-10 w-10 rounded-xl" />
                            <div className="flex flex-col gap-1">
                              <Skeleton className="h-5 w-8" />
                              <Skeleton className="h-3 w-10" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-10 w-10 rounded-xl" />
                            <div className="flex flex-col gap-1">
                              <Skeleton className="h-5 w-8" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-10 w-10 rounded-xl" />
                            <div className="flex flex-col gap-1">
                              <Skeleton className="h-5 w-12" />
                              <Skeleton className="h-3 w-10" />
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-5 border-t border-border/50">
                          <div className="flex gap-2">
                            <Skeleton className="h-9 flex-1 rounded-md" />
                            <Skeleton className="h-9 flex-1 rounded-md" />
                            <Skeleton className="h-9 flex-1 rounded-md" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : displayedProperties?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                  {displayedProperties?.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <h3 className="text-xl font-semibold mb-2">No properties found</h3>
                  <p className="text-muted-foreground mb-6">Try adjusting your search criteria</p>
                  <Button onClick={resetFilters}>Reset Filters</Button>
                </div>
              )}
              {/* Pagination */}
              <div className="mt-12 flex justify-center">
                <PaginationControl
                  totalPages={totalPages}
                  initialPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      <div className={`lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-50 transition-opacity duration-300 ${filterDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className={`fixed inset-y-0 right-0 w-full max-w-xs bg-background p-6 shadow-lg transform transition-transform duration-300 ${filterDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Filters</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setFilterDrawerOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="mb-6 space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={resetFilters}
            >
              Reset All
            </Button>
            <Button
              className="w-full"
              onClick={applyFilters}
            >
              Show {filteredCount} Properties
            </Button>
          </div>

          <div className="space-y-6 overflow-y-auto max-h-[calc(100vh-140px)]">
            {/* Listing Type Filter */}
            <div>
              <h4 className="font-medium mb-3">Listing Type</h4>
              <div className="flex gap-2">
                <Button
                  variant={listingType === 'SALE' ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setListingType(listingType === 'SALE' ? null : 'SALE')}
                >
                  Buy
                </Button>
                <Button
                  variant={listingType === 'RENT' ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setListingType(listingType === 'RENT' ? null : 'RENT')}
                >
                  Rent
                </Button>
                <Button
                  variant={listingType === 'OFF-PLAN' ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setListingType(listingType === 'OFF-PLAN' ? null : 'OFF-PLAN')}
                >
                  Off-Plan
                </Button>
              </div>
            </div>

            {/* Price Range Filter */}
            <div>
              <h4 className="font-medium mb-3">Price Range</h4>
              <Slider
                defaultValue={[0, 15000000]}
                value={priceRange}
                onValueChange={(value) => setPriceRange(value as [number, number])}
                min={0}
                max={15000000}
                step={100000}
                className="mb-6"
              />
              <div className="flex justify-between text-sm">
                <span>{formatPrice(priceRange[0])}</span>
                <span>{formatPrice(priceRange[1])}</span>
              </div>
            </div>

            {/* Property Type Filter */}
            <div>
              <h4 className="font-medium mb-3">Property Type</h4>
              <div className="space-y-3">
                {propertyTypes.map(type => (
                  <div key={type.value} className="flex items-center">
                    <Checkbox
                      id={`mobile-type-${type.value}`}
                      checked={selectedPropertyTypes.includes(type.value)}
                      onCheckedChange={() => togglePropertyType(type.value)}
                    />
                    <label
                      htmlFor={`mobile-type-${type.value}`}
                      className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {type.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Filter */}
            <div>
              <h4 className="font-medium mb-3">Location</h4>
              <div className="space-y-3">
                {localities.map(location => (
                  <div key={location.value} className="flex items-center">
                    <Checkbox
                      id={`mobile-location-${location.value}`}
                      checked={selectedLocations.includes(location.value)}
                      onCheckedChange={() => toggleLocation(location.value)}
                    />
                    <label
                      htmlFor={`mobile-location-${location.value}`}
                      className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {location.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Bedrooms Filter */}
            <div>
              <h4 className="font-medium mb-3">Bedrooms</h4>
              <div className="grid grid-cols-3 gap-2">
                {bedroomOptions.map(bed => (
                  <Button
                    key={bed.value}
                    variant={selectedBedrooms === bed.value ? "default" : "outline"}
                    className="text-sm h-9"
                    onClick={() => setSelectedBedrooms(selectedBedrooms === bed.value ? null : bed.value)}
                  >
                    {bed.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Amenities Filter */}
            <div>
              <h4 className="font-medium mb-3">Amenities</h4>
              <div className="space-y-3">
                {amenities.slice(0, 5).map(amenity => (
                  <div key={amenity.value} className="flex items-center">
                    <Checkbox
                      id={`mobile-amenity-${amenity.value}`}
                      checked={selectedAmenities.includes(amenity.value)}
                      onCheckedChange={() => toggleAmenity(amenity.value)}
                    />
                    <label
                      htmlFor={`mobile-amenity-${amenity.value}`}
                      className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {amenity.label}
                    </label>
                  </div>
                ))}

                <details className="group">
                  <summary className="flex cursor-pointer items-center text-sm font-medium text-muted-foreground">
                    Show more
                    <ChevronDown className="ml-1 h-4 w-4 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mt-3 space-y-3">
                    {amenities.slice(5).map(amenity => (
                      <div key={amenity.value} className="flex items-center">
                        <Checkbox
                          id={`mobile-amenity-${amenity.value}`}
                          checked={selectedAmenities.includes(amenity.value)}
                          onCheckedChange={() => toggleAmenity(amenity.value)}
                        />
                        <label
                          htmlFor={`mobile-amenity-${amenity.value}`}
                          className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {amenity.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            </div>
          </div>

        </div>
      </div>


    </>
  );
};

export default Properties;

